import { useNavigate } from 'react-router-dom';
import { Button, ProgressBar, Badge } from '@dreamtree-org/twreact-ui';
import { Inbox, Wrench, TriangleAlert, Hourglass, ChevronRight, Truck, PackageCheck } from 'lucide-react';
import { PageHeader, StatCard, SectionCard, QueryState, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { hoursLeft } from '../domain';
import { StageBadge, SlaBadge, PriorityBadge, TicketStatusBadge, formatTime, formatDateTime } from '../components';

/** A full-width tappable list row (≥44px) that navigates. */
function RowLink({ onClick, children }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full min-h-[44px] flex items-center gap-3 py-2 text-left rounded-md hover:bg-[var(--app-bg)] px-1"
      >
        <div className="flex-1 min-w-0">{children}</div>
        <ChevronRight size={16} className="opacity-50 shrink-0" aria-hidden />
      </button>
    </li>
  );
}

/** SERV-01 Service overview: incoming tickets, jobs by stage, SLA, capacity, today's runs. */
export default function Overview() {
  const nav = useNavigate();
  const q = useQuery('serviceOverview', null, { select: (r) => r.data });
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Service overview"
        subtitle={`${today} · Indiranagar workshop`}
        actions={<Button onClick={() => nav('/workbench')} leftIcon={<Wrench size={16} />}>Open workbench</Button>}
      />
      <QueryState query={q}>
        {(d) => {
          const maxStage = Math.max(1, ...d.byStage.map((s) => s.count));
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard label="Incoming tickets" value={d.counts.incomingTickets} hint="new, triaged or pickup due" icon={<Inbox size={18} />} />
                <StatCard label="Open work orders" value={d.counts.openJobs} hint="not yet closed" icon={<Wrench size={18} />} />
                <StatCard label="SLA at risk" value={d.counts.atRisk} hint="at risk or breached" icon={<TriangleAlert size={18} />} />
                <StatCard label="Awaiting approval" value={d.counts.awaitingApproval} hint="quotes with customers" icon={<Hourglass size={18} />} />
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <SectionCard title="Jobs by stage" actions={<Button size="sm" variant="ghost" onClick={() => nav('/workbench')}>Board</Button>}>
                  <ul className="space-y-3">
                    {d.byStage.map((s) => (
                      <li key={s.stage} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <StageBadge stage={s.stage} />
                          <span className="font-semibold tabular-nums">{s.count}</span>
                        </div>
                        <ProgressBar value={s.count} max={maxStage} size="sm" ariaLabel={`${s.label}: ${s.count} jobs`} />
                      </li>
                    ))}
                  </ul>
                </SectionCard>

                <SectionCard title="SLA at risk">
                  {d.atRisk.length === 0 ? (
                    <p className="text-sm opacity-70">Every open job is on track.</p>
                  ) : (
                    <ul className="divide-y divide-[var(--app-border)]">
                      {d.atRisk.map((j) => {
                        const h = hoursLeft(j);
                        return (
                          <RowLink key={j.id} onClick={() => nav(`/jobs/${j.id}`)}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold">{j.id}</span>
                              <SlaBadge sla={j.sla} />
                              <StageBadge stage={j.stage} />
                            </div>
                            <div className="text-sm opacity-80 truncate">
                              {j.device.model} · {j.problem}
                            </div>
                            <div className="text-xs opacity-70">
                              {h >= 0 ? `${h} h left` : `${-h} h overdue`} · {j.technicianName}
                            </div>
                          </RowLink>
                        );
                      })}
                    </ul>
                  )}
                </SectionCard>

                <SectionCard title="Technician capacity" actions={<span className="text-xs opacity-70">open-job hours / daily capacity</span>}>
                  <ul className="space-y-3">
                    {d.technicians.map((t) => (
                      <li key={t.id} className="space-y-1">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="font-medium truncate">{t.name}</span>
                          <span className="flex items-center gap-2 shrink-0">
                            {t.percent > 100 ? <Badge variant="error" size="sm">Over</Badge> : null}
                            <span className="tabular-nums opacity-80">
                              {t.plannedHours}/{t.capacityHours} h
                            </span>
                          </span>
                        </div>
                        <ProgressBar value={Math.min(t.percent, 100)} size="sm" ariaLabel={`${t.name} utilisation ${t.percent}%`} />
                        <div className="text-xs opacity-60">
                          {t.activeJobs} open job{t.activeJobs === 1 ? '' : 's'} · {t.skills.join(', ')}
                        </div>
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SectionCard title="Incoming tickets" actions={<Button size="sm" variant="ghost" onClick={() => nav('/tickets')}>All tickets</Button>}>
                  {d.incoming.length === 0 ? (
                    <EmptyState title="No new tickets" />
                  ) : (
                    <ul className="divide-y divide-[var(--app-border)]">
                      {d.incoming.map((t) => (
                        <RowLink key={t.id} onClick={() => nav(`/tickets/${t.id}`)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{t.id}</span>
                            <PriorityBadge priority={t.priority} />
                            <TicketStatusBadge status={t.status} />
                          </div>
                          <div className="text-sm opacity-80 truncate">
                            {t.customer.name} · {t.device.model}
                          </div>
                          <div className="text-xs opacity-70 truncate">
                            {t.problem} · {t.location.area} · {formatDateTime(t.openedAt)}
                          </div>
                        </RowLink>
                      ))}
                    </ul>
                  )}
                </SectionCard>

                <SectionCard title="Today's pickups and deliveries" actions={<Button size="sm" variant="ghost" onClick={() => nav('/field')}>Field jobs</Button>}>
                  {d.today.length === 0 ? (
                    <EmptyState title="Nothing scheduled today" />
                  ) : (
                    <ul className="divide-y divide-[var(--app-border)]">
                      {d.today.map((v) => (
                        <RowLink key={v.id} onClick={() => nav('/field')}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold tabular-nums">{formatTime(v.scheduledAt)}</span>
                            <Badge variant={v.kind === 'pickup' ? 'primary' : 'success'} size="sm">
                              {v.kind === 'pickup' ? <Truck size={12} className="inline mr-1" aria-hidden /> : <PackageCheck size={12} className="inline mr-1" aria-hidden />}
                              {v.kind === 'pickup' ? 'Pickup' : 'Delivery'}
                            </Badge>
                            {v.status === 'done' ? <Badge variant="success" size="sm">Done</Badge> : null}
                          </div>
                          <div className="text-sm opacity-80 truncate">
                            {v.customer.name} · {v.location.area}
                          </div>
                          <div className="text-xs opacity-70 truncate">
                            {v.device.model} · {v.technicianName} · slot {v.slot}
                          </div>
                        </RowLink>
                      ))}
                    </ul>
                  )}
                </SectionCard>
              </div>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
